import { c as _c } from "react/compiler-runtime";
import { RefCallback, useCallback, useRef } from "react";
const prefersReducedMotion = typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia("(prefers-reduced-motion: reduce)") : null;

/**
 * Hook for managing focus in filter input components.
 * Provides a callback ref that auto-focuses the input when mounted in a dropdown context.
 *
 * Primary focus is handled by onAnimationEnd on the content wrapper.
 * When prefers-reduced-motion is active (animations disabled), the callback
 * ref falls back to rAF-based focus since animationend won't fire.
 */
export function useFilterFocus() {
  const $ = _c(2);
  const inputRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = node => {
      (inputRef as React.MutableRefObject<HTMLElement | null>).current = node;
      if (node && prefersReducedMotion?.matches) {
        requestAnimationFrame(() => node.focus());
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const focusRef = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      inputRef,
      focusRef
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

/**
 * Hook for handling back navigation in filter inputs.
 * Calls onBack when ArrowLeft is pressed at the start of the input.
 */
export function useFilterBackNavigation(onBack) {
  const $ = _c(4);
  let t0;
  if ($[0] !== onBack) {
    t0 = e => {
      if (e.key === "ArrowLeft" && onBack) {
        const target = e.target as HTMLInputElement;
        if (target.selectionStart === 0 && target.selectionEnd === 0) {
          e.preventDefault();
          onBack();
        }
      }
    };
    $[0] = onBack;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const handleKeyDown = t0;
  let t1;
  if ($[2] !== handleKeyDown) {
    t1 = {
      handleKeyDown
    };
    $[2] = handleKeyDown;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}